import { useEffect, useState } from "react";
import { Calendar, Col, Row, Spin } from "antd";
import newStyled from "@emotion/styled";
import dayjs from "dayjs";
import moment from "moment/moment";
import "../../styles/UpAudits.css";
import { useFetchFunction } from "../../utils/hooks/UseFetchUrl";
import { Popover } from "antd";

const months = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

const monthsTxt = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "June",
  "July",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec"
];

const UpAudits = ({ showDays = true, showMonths = true, showCalender = true, showUpcomingAudits = true, onDateClick = () => { } }) => {
  const { loading: upcomingLoading, data: upcomingData, getData: getUpcomingData } = useFetchFunction();

  // ==============================================  States =================================================
  const currentDate = new Date();
  const currentYear = currentDate.getFullYear();
  const currentMonthIndex = currentDate.getMonth();
  const currentDay = currentDate.getDate();
  const [selectedDate, setSelectedDate] = useState(currentDay);
  const [activeMonth, setActiveMonth] = useState(months[currentMonthIndex]);
  const [preNextDate, setPreNextDate] = useState(new Date());
  const [auditsByDate, setAuditsByDate] = useState({});
  // const [audits, setAudits] = useState([]);

  const year = currentYear;
  const monthIndex = months.indexOf(activeMonth);
  const daysInMonth = new Date(year, monthIndex + 1, 0).getDate();
  const daysOfWeek = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
  const displayDate = (preNextDate || currentDate).toDateString();
  const isSmallScreen = window.matchMedia("(max-width: 765px)").matches;
  const displayMonthsTxt = isSmallScreen ? monthsTxt : months;

  // ============================================== useEffects =================================================
  useEffect(() => {
    getUpcomingData("/audit_store/upcoming");
  }, []);

  useEffect(() => {
    if (upcomingData) {
      const groupedAudits = upcomingData.reduce((acc, audit) => {
        const date = audit.audit_date;
        if (!acc[date]) acc[date] = [];
        acc[date].push(audit);
        return acc;
      }, {});
      setAuditsByDate(groupedAudits);
    }
  }, [upcomingData]);

  useEffect(() => {
    if (monthsTxt.indexOf(activeMonth) === currentMonthIndex) {
      setSelectedDate(currentDay);
    }
  }, [activeMonth, currentMonthIndex, currentDay, monthsTxt]);

  // ============================================== Functions =================================================
  const getDayOfWeek = (day) => {
    const date = new Date(year, monthIndex, day);
    return daysOfWeek[date.getDay()];
  };

  const handleDateClick = (day) => {
    setSelectedDate(day);

    const selectedDateStr = `${year}-${String(monthIndex + 1).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
    const upcomingAudits = upcomingData.filter((audit) => {
      const auditDate = new Date(audit.date);
      return audit.isActive && auditDate >= currentDate;
    });

    // setAudits(upcomingAudits);
  };

  const customDateCellRender = (value) => {
    const isCurrentMonth = value.month() === monthIndex;
    const dateStr = value.format("YYYY-MM-DD");
    const auditCount = auditsByDate[dateStr]?.length || 0;
    const isSelected =
      value.date() === selectedDate &&
      value.month() === monthIndex &&
      value.year() === year;

    const tooltipContent = auditsByDate[dateStr]
      ? auditsByDate[dateStr].map((audit, index) => {
        const storeName = audit?.audit?.store?.name || "Store name:";
        const storeCityName = audit?.audit?.store?.city?.name || "City name:";
        return (
          <div key={index}>
            <strong className="tooltip-label">
              {index + 1}. Store name:{" "}
            </strong>
            <span className="tooltip-value">{storeName}</span>
            <br />
            <strong className="tooltip-label">City name: </strong>
            <span className="tooltip-value">{storeCityName}</span>
            <p></p>
          </div>
        );
      })
      : [];

    return (
      <div
        style={{
          textAlign: "center",
          backgroundColor: isSelected ? "#007DC1" : "transparent",
          borderRadius: isSelected ? "2px" : "0px",
          color: isCurrentMonth ? (isSelected ? "#fff" : "#000") : "#ccc",
          cursor: isCurrentMonth ? "pointer" : "not-allowed",
          padding: isCurrentMonth ? (isSelected ? "1rem" : "1rem 0rem") : "1rem",
          position: "relative",
        }}
        onClick={() => {
          if (isCurrentMonth) {
            setSelectedDate(value.date());

            // NEW: notify parent which date was clicked, with that date's audits
            if (typeof onDateClick === "function") {
              onDateClick(dateStr, auditsByDate[dateStr] || []);
            }
          }
        }}
      >
        {auditCount > 0 ? (
          <Popover
            content={
              <div style={{ maxHeight: "200px", overflowY: "auto", width: "220px", fontSize: "12px" }}>
                {tooltipContent}
              </div>
            }
            title={`${auditCount} ${auditCount === 1 ? "Audit" : "Audits"}`}
            trigger="hover"
            placement="right"
          >
            <div className="celender-aduitValue-container">
              <div className={`auditCountBg ${isSelected ? "selected" : ""}`}>{auditCount}</div>
              <div className={`auditTxt ${isSelected ? "selected" : ""}`}>{auditCount === 1 ? "audit" : "audits"}</div>
            </div>
          </Popover>
        ) : (
          value.date()
        )}
      </div>
    );
  };

  const upcomingAudits = upcomingData.map((item) => ({
    id: item.id,
    cycle: item.audit.audit_cycle.name,
    city: item.audit.store.city.name,
    details: item.audit.store.name,
    type: item.audit.audit_cycle.type,
    dateOfAudit: item?.audit_date,
    isActive: new Date(item.audit_date) >= new Date(),
  }));

  const handlePreNextChange = (direction) => {
    const newDate = preNextDate ? new Date(preNextDate) : new Date(currentDay);
    newDate.setDate(newDate.getDate() + direction);
    setPreNextDate(newDate);
  };

  const handleMonthChange = (month) => {
    const newMonthIndex = monthsTxt.indexOf(month);
    setActiveMonth(month);
    if (newMonthIndex === currentMonthIndex) {
      setSelectedDate(currentDay);
    } else {
      setSelectedDate(1);
    }
  };

  const truncateStoreName = (name) => {
    const words = name.split(" ");
    if (words.length > 2) {
      return `${words.slice(0, 2).join(" ")}...`;
    }
    return name;
  };

  return (
    <div className={`heroSection  ${showUpcomingAudits ? "row px-4 p-3" : ""}`}>
      <div className={showUpcomingAudits ? "hero-right1 col-md-9" : "col-md-12"}>
        {/* Days */}
        {showDays && (
          <div className="date-picker-container">
            <div className="dates">
              {Array.from({ length: daysInMonth }, (_, i) => i + 1)
                .slice((selectedDate || currentDay) - 1)
                .concat(
                  Array.from({ length: daysInMonth }, (_, i) => i + 1).slice(
                    0,
                    (selectedDate || currentDay) - 1
                  )
                )
                .map((day) => (
                  <div
                    key={day}
                    className={`date-item ${selectedDate === day ? "selected" : ""
                      }`}
                    onClick={() => handleDateClick(day)}
                  >
                    <div className="date-number">{day}</div>
                    <div className="date-number">{getDayOfWeek(day)}</div>
                  </div>
                ))}
            </div>
          </div>
        )}

        {/* Months */}
        {showMonths && (
          <div className="month-tabs d-flex justify-content-center align-items-center mt-4">
            <div className="month-div d-flex justify-content-center align-items-center">
              {displayMonthsTxt
                .slice(currentMonthIndex, currentMonthIndex + 3)
                .map((month, index) => (
                  <div
                    key={index}
                    className={`month my-1 justify-content-center align-items-center`}
                    onClick={() => handleMonthChange(month)}
                  >
                    <div
                      className={`month-span ${activeMonth === month ? "active" : ""
                        } d-flex mx-auto`}
                    >
                      <p className="monthTxt mx-auto m-0">{month}</p>
                    </div>
                  </div>
                ))}
            </div>
          </div>
        )}

        {/* LHS Calender */}
        {showCalender && (
          upcomingLoading ? (
            <div style={{ textAlign: "center", padding: "6rem" }}>
              <Spin size="large" />
            </div>
          ) : (
            <div className="ant-calendar-container mt-2">
              <CalStyle
                fullscreen={false}
                headerRender={() => null}
                fullCellRender={customDateCellRender}
                mode="month"
                value={dayjs(new Date(year, monthIndex, selectedDate))}
              />
            </div>
          )
        )}
      </div>

      {showUpcomingAudits && (
        <div className="hero-left col-md-3">
          <div
            style={{ backgroundColor: "white" }}
            className="audit-head align-items-center p-3 d-flex"
          >
            <p className="my-2">Upcoming Audits</p>
          </div>
          <div
            className="left-main-container"
            style={{ height: "calc(80vh - 80px)", overflowY: "auto" }}
          >
            <div className="left-main p-3">
              <div className="acycle text-start my-2"></div>
              <div className="acycle text-start my-2">
                {upcomingLoading ? (
                  <div style={{ padding: "4rem" }}>
                    <Spin />
                  </div>
                ) : upcomingAudits.length > 0 ? (
                  <div className="audit-cards">
                    {upcomingAudits.map((audit, index) => (
                      <div
                        key={index}
                        className={`audit-card ${audit.isActive ? "active" : "active"
                          }`}
                      >
                        <Row gutter={[16, 16]}>
                          <Col>
                            <p style={{ display: "flex", alignItems: "center" }}>
                              <strong style={{ width: 100 }}>Store Name:</strong>
                              <span>{truncateStoreName(audit.details)}</span>
                            </p>

                            <p style={{ display: "flex", alignItems: "center" }}>
                              <strong style={{ width: 100 }}>City Name:</strong>
                              <span>{audit?.city}</span>
                            </p>

                            <p style={{ display: "flex", alignItems: "center" }}>
                              <strong style={{ width: 100 }}>Audit Date:</strong>
                              <span>{moment(audit.dateOfAudit).format("DD/MMM/YYYY")}</span>
                            </p>
                          </Col>
                        </Row>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="noUpText">There are no upcoming audits.</p>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const CalStyle = newStyled(Calendar)`
:where(.css-dev-only-do-not-override-1kf000u).ant-picker-calendar .ant-picker-panel{
   border-top: none !important;
}
`;

export default UpAudits;
